<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    My Assignments
                </h1>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center items-center py-12">
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ error }}
                        </p>
                        <button
                            @click="clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Assignments List -->
            <div
                v-if="!loading && !error"
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
                <div
                    v-for="assignment in assignments"
                    :key="assignment.id"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                    @click="viewAssignment(assignment)"
                >
                    <div class="p-6">
                        <div class="flex items-start justify-between mb-4">
                            <h3
                                class="text-lg font-semibold text-gray-900 dark:text-white"
                            >
                                {{ assignment.title }}
                            </h3>
                            <span
                                v-if="assignment.is_published"
                                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            >
                                Published
                            </span>
                            <span
                                v-else
                                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            >
                                Draft
                            </span>
                        </div>

                        <p
                            v-if="assignment.description"
                            class="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2"
                        >
                            {{ assignment.description }}
                        </p>

                        <div class="space-y-2 text-sm">
                            <div
                                class="flex items-center text-gray-600 dark:text-gray-400"
                            >
                                <FileText
                                    class="h-4 w-4 mr-2"
                                    :class="{
                                        'text-blue-600 dark:text-blue-400':
                                            assignment.has_attachment,
                                        'text-gray-400 dark:text-gray-500':
                                            !assignment.has_attachment,
                                    }"
                                />
                                <span
                                    :class="{
                                        'text-blue-600 dark:text-blue-400':
                                            assignment.has_attachment,
                                        'text-gray-400 dark:text-gray-500':
                                            !assignment.has_attachment,
                                    }"
                                >
                                    {{
                                        assignment.has_attachment
                                            ? "Has Attachment"
                                            : "No Attachment"
                                    }}
                                </span>
                            </div>

                            <div
                                class="flex items-center text-gray-600 dark:text-gray-400"
                            >
                                <CheckCircle2
                                    class="h-4 w-4 mr-2"
                                    :class="{
                                        'text-green-600 dark:text-green-400':
                                            assignment.submitted_at,
                                        'text-gray-400 dark:text-gray-500':
                                            !assignment.submitted_at,
                                    }"
                                />
                                <span
                                    :class="{
                                        'text-green-600 dark:text-green-400':
                                            assignment.submitted_at,
                                        'text-gray-400 dark:text-gray-500':
                                            !assignment.submitted_at,
                                    }"
                                >
                                    {{
                                        assignment.submitted_at
                                            ? "Submitted"
                                            : "Not Submitted"
                                    }}
                                </span>
                            </div>

                            <div
                                v-if="
                                    assignment.submissions_count !== undefined
                                "
                                class="flex items-center text-gray-600 dark:text-gray-400"
                            >
                                <FileText class="h-4 w-4 mr-2" />
                                <span>
                                    Submissions:
                                    {{ assignment.submissions_count || 0 }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div
                v-if="!loading && !error && assignments.length === 0"
                class="text-center py-12"
            >
                <FileText class="mx-auto h-12 w-12 text-gray-400" />
                <h3
                    class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    No assignments found
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    You don't have any assignments yet.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { CheckCircle2, FileText } from "@lucide/vue";
import assignmentApi, { Assignment } from "@/api/assignment";

const router = useRouter();

const assignments = ref<Assignment[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function fetchAssignments() {
    loading.value = true;
    error.value = null;

    try {
        assignments.value = await assignmentApi.getStudentAssignments();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch assignments";
    } finally {
        loading.value = false;
    }
}

function clearError() {
    error.value = null;
}

function viewAssignment(assignment: Assignment) {
    router.push(`/student-assignments/${assignment.id}`);
}

onMounted(() => {
    fetchAssignments();
});
</script>
